import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import { Button, Card } from '@/components/common';
import { getErrorMessage } from '@/services';
import { insurerReceiptsService } from '@/services/insurerReceipts';
import { ledgerMatchService } from '@/services/ledgerMatch';
import { formatCurrency, formatDate } from '@/utils';
import { currentFinancialYear } from './pospLedgerStatement';
import { ReconciliationTabs } from './ReconciliationTabs';

/**
 * #696 -- Reconciliation > Insurer ledger. One insurer, one financial year (1 Apr - 31 Mar), same shape as the
 * POSP ledger: "Balance b/f", commission earned (Dr), cash and TDS received (Cr) with the running balance
 * (Dr = the insurer owes us), "Balance c/f". Policy rows carry earned / received / TDS / outstanding.
 */
const SELECT = 'rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';
const money = (v: string | null | undefined) => (v ? formatCurrency(Number(v)) : '');

export function InsurerLedgerPage() {
  const [insurer, setInsurer] = useState('');
  const [fy, setFy] = useState(currentFinancialYear());
  const [busy, setBusy] = useState<'csv' | 'pdf' | null>(null);
  const insurers = useQuery({ queryKey: ['receiptInsurers'], queryFn: () => insurerReceiptsService.insurers() });
  const statement = useQuery({
    queryKey: ['insurerLedger', insurer, fy],
    queryFn: () => ledgerMatchService.insurerStatement(insurer, fy),
    enabled: !!insurer,
  });
  const data = statement.data;
  const years = Array.from(new Set([...(data?.financial_years ?? []), fy])).sort().reverse();

  const download = async (kind: 'csv' | 'pdf') => {
    setBusy(kind);
    try {
      await ledgerMatchService.exportInsurerStatement(insurer, fy, kind);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Insurer ledger" subtitle="Commission earned, received and still to come from one insurer, by financial year" />
      <ReconciliationTabs />
      <Card padding="md">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm text-gray-700 dark:text-gray-300">
            <span className="mb-1 block">Insurer</span>
            <select aria-label="Insurer" className={SELECT} value={insurer} onChange={(e) => setInsurer(e.target.value)}>
              <option value="">Choose an insurer</option>
              {(insurers.data ?? []).map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </label>
          <label className="text-sm text-gray-700 dark:text-gray-300">
            <span className="mb-1 block">Financial year</span>
            <select aria-label="Financial year" className={SELECT} value={fy} onChange={(e) => setFy(e.target.value)}>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" isLoading={busy === 'csv'} disabled={!data} onClick={() => download('csv')}>Export CSV</Button>
            <Button variant="outline" size="sm" isLoading={busy === 'pdf'} disabled={!data} onClick={() => download('pdf')}>Export PDF</Button>
          </div>
        </div>
      </Card>

      {!insurer && <p className="text-sm text-gray-500 dark:text-gray-400">Choose an insurer to see the ledger.</p>}
      {statement.isError && <p role="alert" className="text-sm text-amber-700 dark:text-amber-400">Could not load this ledger. That is not the same as there being no entries - please try again.</p>}
      {data && (
        <Card padding="none">
          <div className="flex flex-wrap gap-6 border-b border-gray-100 px-6 py-4 text-sm dark:border-gray-800">
            <div><p className="text-xs text-gray-500 dark:text-gray-400">{data.balance_cf.label}</p><p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{data.balance_cf.text}</p></div>
            <div><p className="text-xs text-gray-500 dark:text-gray-400">Outstanding on this year&apos;s policies</p><p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(Number(data.outstanding_total))}</p></div>
            <div><p className="text-xs text-gray-500 dark:text-gray-400">Received, not yet put against a month</p><p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(Number(data.on_account))}</p></div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{data.date_from} to {data.date_to}<br />{data.note}</div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700" aria-label="Insurer ledger">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  {['Date', 'Entry', 'Policy', 'Customer', 'Dr', 'Cr', 'Balance', 'Earned', 'Received', 'TDS', 'Outstanding'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                <tr data-testid="balance-bf" className="font-medium text-gray-900 dark:text-gray-100">
                  <td className="px-4 py-3 whitespace-nowrap">{formatDate(data.balance_bf.date)}</td>
                  <td className="px-4 py-3" colSpan={5}>{data.balance_bf.label}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{data.balance_bf.text}</td>
                  <td colSpan={4} />
                </tr>
                {data.results.map((r) => (
                  <tr key={r.id} className="text-gray-800 dark:text-gray-200">
                    <td className="px-4 py-3 whitespace-nowrap">{formatDate(r.date)}</td>
                    <td className="px-4 py-3">{r.entry_label}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{r.policy_id ? <Link className="underline" to={`/policies/${r.policy_id}`}>{r.policy_number}</Link> : r.policy_number ?? r.reference ?? ''}</td>
                    <td className="px-4 py-3">{r.customer ?? ''}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{money(r.debit)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{money(r.credit)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{r.running_text}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{money(r.earned)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{money(r.received)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{money(r.tds)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{money(r.outstanding)}</td>
                  </tr>
                ))}
                <tr data-testid="balance-cf" className="font-semibold text-gray-900 dark:text-gray-100">
                  <td className="px-4 py-3 whitespace-nowrap">{formatDate(data.balance_cf.date)}</td>
                  <td className="px-4 py-3" colSpan={3}>{data.balance_cf.label}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatCurrency(Number(data.totals.debit))}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatCurrency(Number(data.totals.credit))}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{data.balance_cf.text}</td>
                  <td colSpan={4} />
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

export default InsurerLedgerPage;
